import { ExclamationCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { Col, Row, Space, Spin, Typography, message } from "antd";
import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useErrorToast } from "../../../api";
import LoginTemplate from "./LoginTemplate";
import { useExchangeSsoToken } from "./api";
import { initializePostLogin } from "./initializePostLogin";

const { Paragraph, Title } = Typography;

export const SSO_CALLBACK_PATH = "/sso/callback";

const SSO_ERROR_MESSAGES: Record<string, string> = {
  email_mismatch: "The Microsoft account email doesn't match the email you entered. Please try again.",
};

export const SSOCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const ssoToken = searchParams.get("ssoToken");
  const errorCode = searchParams.get("error");
  const ssoError = errorCode ? SSO_ERROR_MESSAGES[errorCode] : null;

  const { loginResult, isLoading, isSuccess, isError, error } = useExchangeSsoToken({
    ssoToken,
    enabled: !errorCode,
  });

  useErrorToast({
    isEnabled: Boolean(errorCode) || isError,
    customError: ssoError,
    error,
    fallbackError: "Unable to complete sign-in. Please try again.",
    onError: () => navigate("/login", { replace: true }),
  });

  const isCompletingLogin = isSuccess && Boolean(loginResult);
  const hasError = Boolean(ssoError) || isError;
  const loginCompletedRef = useRef(false);

  useEffect(() => {
    if (errorCode) return;

    if (!ssoToken) {
      message.error("Invalid sign-in attempt. Please try again.");
      navigate("/login", { replace: true });
      return;
    }

    if (isLoading) return;
    if (!loginResult || loginCompletedRef.current) return;

    loginCompletedRef.current = true;

    initializePostLogin(loginResult, { dispatch, navigate, location });
  }, [errorCode, ssoToken, isLoading, loginResult, dispatch, navigate, location]);

  const title = hasError ? "Sign-in failed" : isCompletingLogin ? "Finalizing your account" : "Signing you in";

  const subtitle = ssoError
    ? `${ssoError}\nRedirecting you to login.`
    : isError
      ? "We could not complete Microsoft sign-in.\nRedirecting you to login."
      : isCompletingLogin
        ? "Authentication complete.\nPreparing your dashboard access..."
        : "Completing Microsoft sign-in...";

  return (
    <LoginTemplate load={false}>
      <Row gutter={[24, 24]} justify="center" style={{ marginTop: "1rem" }}>
        <Col span={24}>
          <Space direction="vertical" size={16} style={{ width: "100%", textAlign: "center" }}>
            {hasError ? (
              <ExclamationCircleOutlined style={{ fontSize: 42, color: "var(--red-400)" }} />
            ) : (
              <Spin indicator={<LoadingOutlined style={{ fontSize: 40, color: "#1677ff" }} spin />} />
            )}

            <Title level={4} style={{ marginBottom: 0 }}>
              {title}
            </Title>
            <Paragraph style={{ marginBottom: 0, whiteSpace: "pre-line" }}>{subtitle}</Paragraph>
          </Space>
        </Col>
      </Row>
    </LoginTemplate>
  );
};
